// End of Period (EOP) report math.
//
// Pure functions shared by the EOP wizard so the accounting treatment is
// testable in isolation:
//
//   Actual COGS               = Beginning Inventory + Purchases + Transfers In
//                               − Transfers Out ± Inventory Adjustments − Ending Inventory
//   Net Sales                 = Gross Sales − Discounts − Comps − Refunds
//   Actual COGS %             = Actual COGS ÷ Net Sales
//   Implied Actual Cost Rate  = Actual COGS ÷ Gross Sales
//   Estimated Cost of Discounts = Discounts × Implied Actual Cost Rate
//   Estimated Cost of Comps     = Comps × Implied Actual Cost Rate
//   COGS Attributed to Paid Sales = Actual COGS − Est. Cost of Discounts − Est. Cost of Comps
//   Adjusted Cost %           = COGS Attributed to Paid Sales ÷ Net Sales
//
// The discount/comp estimates are allocations of Actual COGS — they never
// change the inventory-based Actual COGS itself. Refunds are shown at sales
// value but receive no automatic cost allocation (a refund may or may not
// have consumed inventory).
//
// Waste is an operational metric only: it is already reflected in inventory
// consumption (counted stock went down), so subtracting it from Actual COGS
// would double-count it. "COGS excluding recorded waste" is offered as a
// separately-labeled analytical figure.

// Identifier persisted with every generated report so historical reports can
// be re-rendered with the exact method that produced them.
export const CALC_METHOD_ID = 'period_actual_cost_allocation_v1';

// Data-status values persisted with optional amounts. A blank field is
// treated as zero (confirmed_zero) — entering nothing means no activity:
//   reported       — a numeric amount was entered or imported
//   confirmed_zero — blank, treated as an intentional zero
//   not_tracked    — the business does not track this data at all
//   not_provided   — the data exists but was not available for this report
// not_tracked / not_provided are no longer offered by the wizard; they remain
// so reports saved by the older confirm-each-blank workflow still render.
export const DATA_STATUS = {
  REPORTED: 'reported',
  CONFIRMED_ZERO: 'confirmed_zero',
  NOT_TRACKED: 'not_tracked',
  NOT_PROVIDED: 'not_provided',
};

// A status is "known" when it contributes a usable number to calculations.
export const statusIsKnown = (s) => s === DATA_STATUS.REPORTED || s === DATA_STATUS.CONFIRMED_ZERO;

const round2 = (n) => Math.round(n * 100) / 100;
const roundPct = (ratio) => Math.round(ratio * 10000) / 100;

export const parseAmount = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const parseOptionalAmount = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Resolve the effective data status of an optional field:
 * an entered amount always means "reported"; otherwise the explicitly chosen
 * status applies; a blank field with no chosen status has no status (null).
 */
export const resolveDataStatus = (value, explicitStatus = null) => {
  if (parseOptionalAmount(value) != null) return DATA_STATUS.REPORTED;
  if (explicitStatus === DATA_STATUS.CONFIRMED_ZERO
    || explicitStatus === DATA_STATUS.NOT_TRACKED
    || explicitStatus === DATA_STATUS.NOT_PROVIDED) return explicitStatus;
  return null;
};

/**
 * The numeric value an optional field contributes to calculations:
 * reported → the entered amount; confirmed_zero → 0; otherwise null
 * (the value is unknown, not zero).
 */
export const statusValue = (value, status) => {
  if (status === DATA_STATUS.REPORTED) return parseOptionalAmount(value) ?? 0;
  if (status === DATA_STATUS.CONFIRMED_ZERO) return 0;
  return null;
};

export function computeActualCOGS({
  beginning = 0,
  purchases = 0,
  transfersIn = 0,
  transfersOut = 0,
  adjustments = 0,
  ending = 0,
} = {}) {
  return round2(beginning + purchases + transfersIn - transfersOut + adjustments - ending);
}

export function computeNetSales({ gross = 0, discounts = 0, comps = 0, refunds = 0 } = {}) {
  return round2(gross - discounts - comps - refunds);
}

// Returns COGS% (e.g. 31.25 for 31.25%), or null when net sales isn't positive.
export function computeCogsPct(actualCOGS, netSales) {
  if (!(netSales > 0)) return null;
  return roundPct(actualCOGS / netSales);
}

/**
 * Implied Actual Cost Rate = Actual COGS ÷ Gross Sales (as a percentage,
 * e.g. 30 for 30%). Gross Sales is the sales value BEFORE discounts, comps,
 * and refunds — never Net Sales. Returns null when either input isn't
 * positive (no valid allocation rate exists).
 */
export function computeImpliedCostRate(actualCOGS, grossSales) {
  if (!(actualCOGS > 0) || !(grossSales > 0)) return null;
  return roundPct(actualCOGS / grossSales);
}

/**
 * Period cost allocation (period_actual_cost_allocation_v1).
 *
 * Converts sales-value discounts and comps into estimated product cost using
 * the category's Implied Actual Cost Rate, then allocates Actual COGS between
 * paid sales and those adjustments. Refunds are intentionally excluded: they
 * are shown at sales value with no automatic cost estimate.
 *
 * `discounts` / `comps` are the known sales-value amounts (pass null when the
 * amount is unknown — i.e. status not_provided / not_tracked). Estimates that
 * depend on an unknown amount come back null, as does COGS Attributed to Paid
 * Sales when either estimate is unavailable.
 *
 * Returns:
 *   {
 *     impliedCostRate,   // % or null
 *     estDiscountCost,   // $ or null
 *     estCompCost,       // $ or null
 *     cogsPaidSales,     // $ or null  (Actual COGS − both estimates)
 *     unavailableReason, // null | 'invalid_cogs' | 'no_gross_sales'
 *   }
 */
export function computeCostAllocation({
  actualCOGS = 0,
  grossSales = null,
  discounts = null,
  comps = null,
} = {}) {
  const none = (reason) => ({
    impliedCostRate: null,
    estDiscountCost: null,
    estCompCost: null,
    cogsPaidSales: null,
    unavailableReason: reason,
  });

  if (!(actualCOGS > 0)) return none('invalid_cogs');
  if (!(grossSales > 0)) return none('no_gross_sales');

  const rate = actualCOGS / grossSales;
  const estDiscountCost = discounts != null ? round2(discounts * rate) : null;
  const estCompCost = comps != null ? round2(comps * rate) : null;
  const cogsPaidSales = (estDiscountCost != null && estCompCost != null)
    ? round2(actualCOGS - estDiscountCost - estCompCost)
    : null;

  return {
    impliedCostRate: roundPct(rate),
    estDiscountCost,
    estCompCost,
    cogsPaidSales,
    unavailableReason: null,
  };
}

// Adjusted Cost % = COGS Attributed to Paid Sales ÷ Net Sales.
// Only defined when both are available and Net Sales is positive.
export function computeAdjustedCostPct(cogsPaidSales, netSales) {
  if (cogsPaidSales == null || !(netSales > 0)) return null;
  return roundPct(cogsPaidSales / netSales);
}

/**
 * Full per-category computation used by the wizard's calculation memo and the
 * review/save step. All monetary inputs may be raw input strings; blank
 * fields are treated as zero. The *Status inputs are explicit data statuses
 * carried by older saved reports (an entered amount always resolves to
 * 'reported').
 */
export function computeCategoryRow({
  beginning,
  ending,
  purchases,
  transfersIn,
  transfersOut,
  adjustments,
  gross,
  discounts,
  comps,
  refunds,
  waste,
  discountsStatus = null,
  compsStatus = null,
  refundsStatus = null,
  wasteStatus = null,
  transfersInStatus = null,
  transfersOutStatus = null,
  salesUnavailable = false,
} = {}) {
  const b = parseAmount(beginning);
  const e = parseAmount(ending);
  const p = parseAmount(purchases);
  const tIn = parseAmount(transfersIn);
  const tOut = parseAmount(transfersOut);
  const adj = parseAmount(adjustments);

  const actualCOGS = computeActualCOGS({
    beginning: b, purchases: p, transfersIn: tIn, transfersOut: tOut, adjustments: adj, ending: e,
  });

  const grossN = parseOptionalAmount(gross);

  // Resolved data statuses (an entered amount wins over any chosen status).
  // A blank field with no explicit status is an intentional zero; explicit
  // not_provided / not_tracked statuses (older saved reports) still apply.
  const dStatus = resolveDataStatus(discounts, discountsStatus) ?? DATA_STATUS.CONFIRMED_ZERO;
  const cStatus = resolveDataStatus(comps, compsStatus) ?? DATA_STATUS.CONFIRMED_ZERO;
  const rStatus = resolveDataStatus(refunds, refundsStatus) ?? DATA_STATUS.CONFIRMED_ZERO;
  const wStatus = resolveDataStatus(waste, wasteStatus) ?? DATA_STATUS.CONFIRMED_ZERO;
  const tInStatus = resolveDataStatus(transfersIn, transfersInStatus) ?? DATA_STATUS.CONFIRMED_ZERO;
  const tOutStatus = resolveDataStatus(transfersOut, transfersOutStatus) ?? DATA_STATUS.CONFIRMED_ZERO;

  // Values: blank fields contribute $0; null only when an explicit
  // not_provided / not_tracked status says the amount is unknown.
  const d = statusValue(discounts, dStatus);
  const c = statusValue(comps, cStatus);
  const r = statusValue(refunds, rStatus);
  const w = statusValue(waste, wStatus);

  // Sales scope is driven by what was actually entered, not by the
  // defaulted-to-zero deduction values.
  const hasSales = !salesUnavailable
    && (grossN != null || parseOptionalAmount(discounts) != null
      || parseOptionalAmount(comps) != null || parseOptionalAmount(refunds) != null);

  // Net Sales is complete when every deduction is reported, blank (zero), or
  // confirmed zero. Only an explicit not_provided / not_tracked status leaves
  // it null — marked incomplete, never presented as $0-adjusted gross.
  const salesComplete = hasSales
    && grossN != null
    && statusIsKnown(dStatus) && statusIsKnown(cStatus) && statusIsKnown(rStatus);
  const netSales = salesComplete
    ? computeNetSales({ gross: grossN, discounts: d, comps: c, refunds: r })
    : null;

  // Actual COGS % against the best valid denominator: confirmed Net Sales
  // when available, otherwise Gross Sales (flagged via cogsPctBasis). A
  // confirmed-but-negative Net Sales yields no percentage at all — falling
  // back to gross would hide the data problem.
  let cogsPct = null;
  let cogsPctBasis = null;
  if (salesComplete) {
    if (netSales > 0) {
      cogsPct = computeCogsPct(actualCOGS, netSales);
      cogsPctBasis = 'net';
    }
  } else if (hasSales && grossN > 0) {
    cogsPct = roundPct(actualCOGS / grossN);
    cogsPctBasis = 'gross';
  }

  // Cost allocation — discounts/comps converted to estimated cost via the
  // Implied Actual Cost Rate. Unknown amounts yield null estimates.
  const allocation = hasSales
    ? computeCostAllocation({ actualCOGS, grossSales: grossN, discounts: d, comps: c })
    : computeCostAllocation({ actualCOGS, grossSales: null });

  const adjustedCostPct = computeAdjustedCostPct(allocation.cogsPaidSales, netSales);

  return {
    beginning: b,
    ending: e,
    purchases: p,
    transfersIn: tIn,
    transfersOut: tOut,
    adjustments: adj,
    netTransfersAdjustments: round2(tIn - tOut + adj),
    transfersInStatus: tInStatus,
    transfersOutStatus: tOutStatus,
    actualCOGS,
    grossSales: hasSales ? grossN : null,
    discounts: hasSales ? d : null,
    comps: hasSales ? c : null,
    refunds: hasSales ? r : null,
    discountsStatus: dStatus,
    compsStatus: cStatus,
    refundsStatus: rStatus,
    waste: w,
    wasteStatus: wStatus,
    salesComplete,
    netSales,
    cogsPct,
    cogsPctBasis,
    cogsExcludingWaste: round2(actualCOGS - (w ?? 0)),
    impliedCostRate: allocation.impliedCostRate,
    estDiscountCost: allocation.estDiscountCost,
    estCompCost: allocation.estCompCost,
    cogsPaidSales: allocation.cogsPaidSales,
    adjustedCostPct,
    allocationUnavailableReason: allocation.unavailableReason,
  };
}
